import { useState } from 'react';
import { Link } from 'react-router-dom';
import { registrationSchema, type RegistrationInput } from '@shared/schemas';
import { supabase } from '@/lib/supabase';

const RECHTSFORMEN = ['GmbH', 'AG', 'KG', 'OHG', 'e.G.', 'UG', 'GmbH & Co. KG', 'e.V.', 'Sonstige'];
const SITZLAENDER = ['DE', 'AT', 'CH', 'FR', 'NL', 'BE', 'IT', 'ES', 'Andere'];

export function RegisterPage() {
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    invitationCode: '',
    companyName: '',
    rechtsform: '',
    sitzland: '',
    handelsregister: '',
    ust_id: '',
    vorname: '',
    nachname: '',
    position: '',
    email: '',
    telefon: '',
    password: '',
    acceptedAGB: false,
    acceptedDatenschutz: false,
    confirmedB2B: false,
  });

  function updateField(field: string, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Manual boolean conversion for Zod
    const data = {
      ...form,
      ust_id: form.ust_id || undefined,
    };

    const validation = registrationSchema.safeParse(data);
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      return;
    }

    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: {
          data: {
            invitation_code: form.invitationCode,
            company_name: form.companyName,
            rechtsform: form.rechtsform,
            sitzland: form.sitzland,
            handelsregister: form.handelsregister,
            ust_id: form.ust_id,
            vorname: form.vorname,
            nachname: form.nachname,
            position: form.position,
            telefon: form.telefon,
          },
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        return;
      }

      setSuccess(true);
    } catch {
      setError('Ein unbekannter Fehler ist aufgetreten.');
    }
  }

  if (success) {
    return (
      <div className="max-w-md mx-auto py-12">
        <h1 className="text-2xl font-serif mb-4">Registrierung erfolgreich</h1>
        <p className="text-text-secondary mb-4">
          Bitte prufen Sie Ihren Posteingang. Sie erhalten in Kurze eine Bestatigungs-E-Mail.
          Nach der E-Mail-Verifikation wird Ihr Konto durch den Administrator freigeschaltet.
        </p>
        <Link to="/anmelden" className="btn-primary">Zur Anmeldung</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-12">
      <h1 className="text-2xl font-serif mb-6">Registrierung</h1>

      {error && (
        <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/20 text-status-danger text-sm rounded-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Invitation & Company */}
        <fieldset className="border border-surface-border p-4">
          <legend className="font-serif text-sm font-semibold">Schritt 1: Einladung und Unternehmen</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <div>
              <label htmlFor="invitationCode" className="block text-sm font-medium mb-1">Einladungscode *</label>
              <input id="invitationCode" className="input-field" value={form.invitationCode} onChange={(e) => updateField('invitationCode', e.target.value)} placeholder="XXXX-XXXX-XXXX" />
            </div>
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium mb-1">Unternehmensname *</label>
              <input id="companyName" className="input-field" value={form.companyName} onChange={(e) => updateField('companyName', e.target.value)} />
            </div>
            <div>
              <label htmlFor="rechtsform" className="block text-sm font-medium mb-1">Rechtsform *</label>
              <select id="rechtsform" className="input-field" value={form.rechtsform} onChange={(e) => updateField('rechtsform', e.target.value)}>
                <option value="">Bitte wahlen</option>
                {RECHTSFORMEN.map((rf) => <option key={rf} value={rf}>{rf}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="sitzland" className="block text-sm font-medium mb-1">Sitzland *</label>
              <select id="sitzland" className="input-field" value={form.sitzland} onChange={(e) => updateField('sitzland', e.target.value)}>
                <option value="">Bitte wahlen</option>
                {SITZLAENDER.map((sl) => <option key={sl} value={sl}>{sl}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="handelsregister" className="block text-sm font-medium mb-1">Handelsregisternummer *</label>
              <input id="handelsregister" className="input-field" value={form.handelsregister} onChange={(e) => updateField('handelsregister', e.target.value)} />
            </div>
            <div>
              <label htmlFor="ust_id" className="block text-sm font-medium mb-1">USt-IdNr. (optional)</label>
              <input id="ust_id" className="input-field" value={form.ust_id} onChange={(e) => updateField('ust_id', e.target.value)} />
            </div>
          </div>
        </fieldset>

        {/* Step 2: Contact person */}
        <fieldset className="border border-surface-border p-4">
          <legend className="font-serif text-sm font-semibold">Schritt 2: Ansprechpartner</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <div>
              <label htmlFor="vorname" className="block text-sm font-medium mb-1">Vorname *</label>
              <input id="vorname" className="input-field" value={form.vorname} onChange={(e) => updateField('vorname', e.target.value)} />
            </div>
            <div>
              <label htmlFor="nachname" className="block text-sm font-medium mb-1">Nachname *</label>
              <input id="nachname" className="input-field" value={form.nachname} onChange={(e) => updateField('nachname', e.target.value)} />
            </div>
            <div>
              <label htmlFor="position" className="block text-sm font-medium mb-1">Position *</label>
              <input id="position" className="input-field" value={form.position} onChange={(e) => updateField('position', e.target.value)} />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1">E-Mail *</label>
              <input id="email" type="email" className="input-field" value={form.email} onChange={(e) => updateField('email', e.target.value)} autoComplete="email" />
            </div>
            <div>
              <label htmlFor="telefon" className="block text-sm font-medium mb-1">Telefon *</label>
              <input id="telefon" className="input-field" value={form.telefon} onChange={(e) => updateField('telefon', e.target.value)} />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1">Passwort *</label>
              <input id="password" type="password" className="input-field" value={form.password} onChange={(e) => updateField('password', e.target.value)} />
              <p className="text-xs text-text-muted mt-1">Minimum 12 Zeichen, 1 Grossbuchstabe, 1 Ziffer, 1 Sonderzeichen</p>
            </div>
          </div>
        </fieldset>

        {/* Step 3: Confirmations */}
        <fieldset className="border border-surface-border p-4">
          <legend className="font-serif text-sm font-semibold">Schritt 3: Bestatigungen</legend>
          <div className="space-y-3 mt-3">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.acceptedAGB} onChange={(e) => updateField('acceptedAGB', e.target.checked)} />
              <span>Ich habe die <a href="/agb" className="">AGB und Auktionsbedingungen</a> gelesen und akzeptiert.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.acceptedDatenschutz} onChange={(e) => updateField('acceptedDatenschutz', e.target.checked)} />
              <span>Ich habe die <a href="/datenschutz" className="">Datenschutzerklarung</a> gelesen und akzeptiert.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={form.confirmedB2B} onChange={(e) => updateField('confirmedB2B', e.target.checked)} />
              <span>Ich bestatige, dass ich fur ein Unternehmen handle und nicht als Verbraucher gemass § 14 BGB.</span>
            </label>
          </div>
        </fieldset>

        <button type="submit" className="btn-primary">
          Registrieren
        </button>
      </form>

      <p className="mt-4 text-sm text-text-secondary">
        Bereits registriert? <Link to="/anmelden" className="">Anmelden</Link>
      </p>
    </div>
  );
}
